import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';

interface Answer {
  question: number;
  answer: string;
}

export default function VisaQuiz() {
  const [scrolled, setScrolled] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const questions = [
    {
      id: 0,
      question: 'What is your primary reason for moving to Ireland?',
      options: [
        { value: 'work', label: 'Employment - I have a job offer' },
        { value: 'study', label: 'Education - I want to study' },
        { value: 'business', label: 'Business - I want to start a business' },
        { value: 'family', label: 'Family reunification' },
        { value: 'retirement', label: 'Retirement' }
      ]
    },
    {
      id: 1,
      question: 'What is your annual salary or expected salary in Ireland?',
      options: [
        { value: 'over64k', label: 'Over €64,000' },
        { value: '32k-64k', label: '€32,000 - €64,000' },
        { value: '30k-32k', label: '€30,000 - €32,000' },
        { value: 'under30k', label: 'Under €30,000' },
        { value: 'na', label: 'Not applicable / No job offer yet' }
      ]
    },
    {
      id: 2,
      question: 'What is your occupation or field of work?',
      options: [
        { value: 'tech', label: 'Technology (Software Engineer, Data Scientist, etc.)' },
        { value: 'healthcare', label: 'Healthcare (Doctor, Nurse, Medical Professional)' },
        { value: 'finance', label: 'Finance & Accounting' },
        { value: 'engineering', label: 'Engineering (Non-Software)' },
        { value: 'education', label: 'Education & Research' },
        { value: 'other', label: 'Other profession' }
      ]
    },
    {
      id: 3,
      question: 'What is your highest level of education?',
      options: [
        { value: 'phd', label: 'PhD or Doctorate' },
        { value: 'masters', label: 'Master\'s Degree' },
        { value: 'bachelors', label: 'Bachelor\'s Degree' },
        { value: 'diploma', label: 'Professional Diploma or Certificate' },
        { value: 'highschool', label: 'High School Diploma' }
      ]
    },
    {
      id: 4,
      question: 'How many years of professional experience do you have?',
      options: [
        { value: '10plus', label: '10+ years' },
        { value: '5-10', label: '5-10 years' },
        { value: '2-5', label: '2-5 years' },
        { value: '1-2', label: '1-2 years' },
        { value: 'less1', label: 'Less than 1 year' }
      ]
    },
    {
      id: 5,
      question: 'Do you have a job offer from an Irish employer?',
      options: [
        { value: 'yes-written', label: 'Yes, I have a written job offer' },
        { value: 'yes-verbal', label: 'Yes, verbal offer only' },
        { value: 'interviewing', label: 'Currently interviewing' },
        { value: 'searching', label: 'Actively searching' },
        { value: 'no', label: 'No, not yet' }
      ]
    }
  ];

  const handleAnswer = (value: string) => {
    const newAnswers = [...answers.filter(a => a.question !== currentQuestion), { question: currentQuestion, answer: value }];
    setAnswers(newAnswers);

    if (currentQuestion < questions.length - 1) {
      setTimeout(() => {
        setCurrentQuestion(currentQuestion + 1);
      }, 300);
    } else {
      setTimeout(() => {
        setShowResults(true);
      }, 300);
    }
  };

  const getRecommendation = () => {
    const answerMap = answers.reduce((acc, curr) => {
      acc[curr.question] = curr.answer;
      return acc;
    }, {} as Record<number, string>);

    const salary = answerMap[1];
    const occupation = answerMap[2];
    const jobOffer = answerMap[5];

    // Critical Skills Employment Permit
    if (
      (salary === 'over64k' || salary === '32k-64k') &&
      (occupation === 'tech' || occupation === 'healthcare' || occupation === 'finance' || occupation === 'engineering') &&
      (jobOffer === 'yes-written' || jobOffer === 'yes-verbal')
    ) {
      return {
        type: 'Critical Skills Employment Permit',
        eligibility: 'Highly Likely',
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        description: 'You appear to be an excellent candidate for the Critical Skills Employment Permit. This is the most advantageous work permit option for Ireland.',
        benefits: [
          'Fast-track processing (typically 8-10 weeks)',
          'Pathway to permanent residency after 2 years',
          'Immediate family reunification rights',
          'No labor market needs test required',
          'Can change employers after 12 months'
        ],
        nextSteps: [
          'Ensure your job offer is in writing with salary details',
          'Verify your occupation is on the Critical Skills Occupations List',
          'Gather educational certificates and employment references',
          'Have your employer prepare the application documents',
          'Consider consulting with an immigration lawyer for guidance'
        ]
      };
    }

    // General Employment Permit
    if (
      (salary === '30k-32k' || salary === '32k-64k') &&
      (jobOffer === 'yes-written' || jobOffer === 'yes-verbal')
    ) {
      return {
        type: 'General Employment Permit',
        eligibility: 'Likely Eligible',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        description: 'You may qualify for a General Employment Permit. Your employer will need to demonstrate they couldn\'t find a suitable EEA candidate.',
        benefits: [
          'Valid for 2 years initially',
          'Can be renewed',
          'Pathway to permanent residency after 5 years',
          'Family reunification possible',
          'Can lead to Critical Skills permit upgrade'
        ],
        nextSteps: [
          'Confirm your employer can conduct a labor market needs test',
          'Ensure salary meets minimum €30,000 requirement',
          'Verify your occupation is not on the ineligible list',
          'Prepare all required documentation',
          'Consider working with an immigration specialist'
        ]
      };
    }

    // Need more preparation
    return {
      type: 'Additional Preparation Needed',
      eligibility: 'Not Yet Eligible',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      description: 'Based on your responses, you may need to strengthen your application before applying for an Irish work permit.',
      benefits: [
        'Time to secure a competitive job offer',
        'Opportunity to gain additional experience',
        'Chance to improve qualifications',
        'Ability to research the Irish job market',
        'Network with Irish employers and recruiters'
      ],
      nextSteps: [
        'Focus on securing a written job offer from an Irish employer',
        'Research companies in Ireland hiring Americans',
        'Consider upskilling in high-demand areas',
        'Network with Irish recruiters and expat communities',
        'Explore job boards like IrishJobs.ie and LinkedIn',
        'Consult with immigration lawyers for alternative pathways'
      ]
    };
  };

  const restartQuiz = () => {
    setCurrentQuestion(0);
    setAnswers([]);
    setShowResults(false);
  };

  const recommendation = showResults ? getRecommendation() : null;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navbar scrolled={scrolled} />
      
      {/* Added pt-24 to account for fixed navbar */}
      <div className="pt-24 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          {!showResults ? (
            <>
              {/* Progress Bar */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-[#0A381F]">
                    Question {currentQuestion + 1} of {questions.length}
                  </span>
                  <span className="text-sm text-gray-500">
                    {Math.round(((currentQuestion + 1) / questions.length) * 100)}% Complete
                  </span>
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#D4AF37] transition-all duration-500"
                    style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
                  ></div>
                </div>
              </div>

              {/* Question Card */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <h2 className="text-3xl font-bold text-[#0A381F] mb-8">
                  {questions[currentQuestion].question}
                </h2>
                <div className="space-y-4">
                  {questions[currentQuestion].options.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleAnswer(option.value)}
                      className="w-full text-left p-5 rounded-xl border-2 border-gray-200 hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transition-all duration-300 cursor-pointer group"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-gray-700 group-hover:text-[#0A381F] font-medium">
                          {option.label}
                        </span>
                        <div className="w-6 h-6 flex items-center justify-center text-gray-400 group-hover:text-[#D4AF37]">
                          <i className="ri-arrow-right-line text-xl"></i>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Back Button */}
              {currentQuestion > 0 && (
                <button
                  onClick={() => setCurrentQuestion(currentQuestion - 1)}
                  className="mt-6 flex items-center gap-2 text-gray-600 hover:text-[#0A381F] transition-colors cursor-pointer whitespace-nowrap"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-arrow-left-line text-xl"></i>
                  </div>
                  Previous Question
                </button>
              )}
            </>
          ) : (
            <>
              {/* Results */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <div className="text-center mb-8">
                  <div className="w-20 h-20 flex items-center justify-center bg-[#0A381F] text-white rounded-full mx-auto mb-4">
                    <i className="ri-checkbox-circle-line text-4xl"></i>
                  </div>
                  <h2 className="text-4xl font-bold text-[#0A381F] mb-4">Your Results</h2>
                  <p className="text-gray-600">Based on your responses, here's our recommendation:</p>
                </div>

                <div className={`${recommendation?.bgColor} border-2 ${recommendation?.borderColor} rounded-xl p-6 mb-8`}>
                  <div className="flex items-center gap-3 mb-4">
                    <h3 className="text-2xl font-bold text-[#0A381F]">{recommendation?.type}</h3>
                    <span className={`${recommendation?.color} font-bold text-lg`}>
                      {recommendation?.eligibility}
                    </span>
                  </div>
                  <p className="text-gray-700 leading-relaxed">{recommendation?.description}</p>
                </div>

                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  <div>
                    <h4 className="text-xl font-bold text-[#0A381F] mb-4">Key Benefits</h4>
                    <ul className="space-y-3">
                      {recommendation?.benefits.map((benefit, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <div className="w-6 h-6 flex items-center justify-center text-[#D4AF37] flex-shrink-0 mt-0.5">
                            <i className="ri-check-line text-xl"></i>
                          </div>
                          <span className="text-gray-700">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-xl font-bold text-[#0A381F] mb-4">Next Steps</h4>
                    <ol className="space-y-3">
                      {recommendation?.nextSteps.map((step, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <div className="w-6 h-6 flex items-center justify-center bg-[#0A381F] text-white rounded-full flex-shrink-0 text-xs font-bold">
                            {index + 1}
                          </div>
                          <span className="text-gray-700">{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>

                <div className="bg-[#0A381F] text-white rounded-xl p-6 mb-6">
                  <h4 className="text-xl font-bold mb-3">Ready to Take Action?</h4>
                  <p className="text-white/90 mb-4">
                    Connect with verified immigration lawyers who specialize in helping Americans move to Ireland. Get personalized guidance for your specific situation.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link
                      to="/directory?category=Legal"
                      className="inline-block bg-[#D4AF37] hover:bg-[#C19B2F] text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap text-center"
                    >
                      Find Immigration Lawyers
                    </Link>
                    <a
                      href="https://www.booking.com"
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="inline-block bg-white text-[#0A381F] px-6 py-3 rounded-lg font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap text-center hover:bg-gray-100"
                    >
                      <i className="ri-plane-line mr-2"></i>
                      Plan Scouting Trip
                    </a>
                  </div>
                </div>

                <div className="flex gap-4">
                  <button
                    onClick={restartQuiz}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-[#0A381F] py-3 rounded-lg font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    Retake Quiz
                  </button>
                  <Link
                    to="/tools"
                    className="flex-1 bg-[#0A381F] hover:bg-[#0A381F]/90 text-white py-3 rounded-lg font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap text-center"
                  >
                    Explore More Tools
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
